import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../api/messaging";
import { unreadByChannel } from "../api/messaging/helpers";
import { useAuth } from "./useAuth";
import { useJourneyLive } from "./useJourneyLive";

const MessagingLiveContext = createContext(null);

const EMPTY_UNREAD = { messageUnread: 0, clientUnread: 0, officeUnread: 0 };

async function loadLiveState(session) {
  if (!session?.userId) {
    return { items: [], transactions: [], ...EMPTY_UNREAD, error: "" };
  }
  const [notices, txs] = await Promise.all([
    api("GET", `/api/notifications/user/${session.userId}`, null, session),
    api("GET", "/api/transactions", null, session),
  ]);
  const transactions = txs.ok ? txs.data || [] : [];
  const items = notices.ok ? notices.data || [] : [];
  const unread = unreadByChannel(transactions, session.userId);
  const error = !notices.ok
    ? notices.error || "Could not load notices"
    : !txs.ok
      ? txs.error || "Could not load conversations"
      : "";
  return { items, transactions, ...unread, error };
}

export function MessagingLiveProvider({ children }) {
  const { session } = useAuth();
  const [items, setItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [messageUnread, setMessageUnread] = useState(0);
  const [clientUnread, setClientUnread] = useState(0);
  const [officeUnread, setOfficeUnread] = useState(0);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    const next = await loadLiveState(session);
    setItems(next.items);
    setTransactions(next.transactions);
    setMessageUnread(next.messageUnread);
    setClientUnread(next.clientUnread);
    setOfficeUnread(next.officeUnread);
    setError(next.error);
    return next;
  }, [session]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useJourneyLive(refresh);

  const markRead = useCallback(
    async (ids) => {
      const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
      if (!list.length || !session) return;
      await api("POST", "/api/notifications/mark-read", { ids: list }, session);
      await refresh();
    },
    [session, refresh]
  );

  const value = useMemo(
    () => ({
      items,
      unread: items.filter((item) => !item.is_read).length,
      messageUnread,
      clientUnread,
      officeUnread,
      transactions,
      error,
      refresh,
      markRead,
    }),
    [items, messageUnread, clientUnread, officeUnread, transactions, error, refresh, markRead]
  );

  return <MessagingLiveContext.Provider value={value}>{children}</MessagingLiveContext.Provider>;
}

export function useNotifications() {
  const context = useContext(MessagingLiveContext);
  if (!context) {
    return {
      items: [],
      unread: 0,
      messageUnread: 0,
      clientUnread: 0,
      officeUnread: 0,
      transactions: [],
      error: "",
      refresh: async () => [],
      markRead: async () => {},
    };
  }
  return context;
}
